import {
  closeSnackbar,
  type CustomContentProps,
  SnackbarContent,
  type VariantType,
} from 'notistack'

import { cn } from '@gto/ui/cn'

type Props = CustomContentProps & {
  ref?: React.Ref<HTMLDivElement>
}

// Plain Tailwind + notistack — `@gto/ui/alert` pulls in `react-intl` for its
// close button, and admin-web doesn't wire `IntlProvider`. Reaching for Alert
// here would crash the tree on every snackbar render and unmount the page
// (observed on Save / Retry: `[React Intl] Could not find required 'intl' object`).
// Only the four registered variants in app.tsx route through this component.
// `default` (what notistack picks with no `variant`) is rendered by
// notistack's built-in, never by us — omitting it keeps the dead branch out.
const VARIANT_CLASS: Record<Exclude<VariantType, 'default'>, string> = {
  success: 'bg-success-main text-success-contrastText',
  error: 'bg-error-main text-error-contrastText',
  warning: 'bg-warning-main text-warning-contrastText',
  info: 'bg-info-main text-info-contrastText',
}

export const AdminSnackbarMessage = ({ id, variant, heading, message, ref }: Props) => {
  const content = typeof heading === 'string' && heading.length > 0 ? heading : message
  // notistack still types `variant` as `VariantType` (includes 'default'), but
  // app.tsx never routes the default through us. Narrow at the call site.
  const variantClass = variant === 'default' ? '' : VARIANT_CLASS[variant]

  return (
    <SnackbarContent ref={ref}>
      <div
        role={variant === 'error' || variant === 'warning' ? 'alert' : 'status'}
        className={cn(
          'flex w-[min(420px,90vw)] items-start gap-3 rounded-md px-4 py-3',
          variantClass,
        )}
      >
        <div className="flex-1 text-14">{content}</div>
        <button
          aria-label="Dismiss"
          className="-mr-1 shrink-0 rounded-sm px-1.5 text-[18px] opacity-80 hover:opacity-100"
          type="button"
          onClick={() => closeSnackbar(id)}
        >
          ×
        </button>
      </div>
    </SnackbarContent>
  )
}
